import React, { useMemo, useState } from "react"
import { Plus } from "lucide-react"

const StatusBadge = ({ label, statusClass }) => (
  <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${statusClass}`}>
    {label}
  </span>
)

const AnnualGoalsAdminList = ({
  adminStaff = [],
  loading = false,
  statusByEmail = {},
  statusesLoading = false,
  currentYear,
  getStatusLabel,
  getStatusClass,
  onInitiateGoalPlanning,
  onInitiateMidYear,
  onInitiateEndYear,
  onOpenConference,
  onOpenCheckInConference,
  onReinitiateGoalPlanning,
  onReinitiateCheckIn,
  onViewGoalPlanning,
  onViewCheckIn,
}) => {
  const [searchQuery, setSearchQuery] = useState("")

  const filteredStaff = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    if (!query) return adminStaff

    return adminStaff.filter((admin) => {
      const fullName = `${admin.firstName || ""} ${admin.lastName || ""}`.toLowerCase()
      const email = (admin.email || "").toLowerCase()
      return fullName.includes(query) || email.includes(query)
    })
  }, [adminStaff, searchQuery])

  const getLatestForYear = (items = [], predicate) => {
    const matches = items.filter(
      (item) => item.year === currentYear && predicate(item)
    )
    if (!matches.length) return null

    const active = matches.find(
      (item) =>
        item.status === "initiated" || item.status === "submitted_by_admin"
    )
    if (active) return active

    const completed = matches.find((item) => item.status === "completed")
    if (completed) return completed

    return matches[0]
  }

  const getAdminStatus = (admin) => {
    const data = statusByEmail[admin.email?.toLowerCase()] || {
      goalPlannings: [],
      checkIns: [],
    }

    const goalPlanning = getLatestForYear(data.goalPlannings, () => true)
    const midYear = getLatestForYear(
      data.checkIns,
      (item) => item.checkInType === "mid_year"
    )
    const endYear = getLatestForYear(
      data.checkIns,
      (item) => item.checkInType === "end_of_year"
    )

    // Goal Planning is "locked" only after the evaluator completes the conference.
    // While initiated / submitted / reinitiated, Mid-Year and End-of-Year stay locked.
    const isGoalPlanningLocked = goalPlanning?.status === "completed"
    const isMidYearLocked = midYear?.status === "completed"

    const hasActiveGoal =
      goalPlanning?.status === "initiated" ||
      goalPlanning?.status === "submitted_by_admin"
    const hasActiveMidYear =
      midYear?.status === "initiated" || midYear?.status === "submitted_by_admin"
    const hasActiveEndYear =
      endYear?.status === "initiated" || endYear?.status === "submitted_by_admin"

    const canActOnMidYear = isGoalPlanningLocked
    const canActOnEndYear = isGoalPlanningLocked && isMidYearLocked
    const canShowMidYearReinitiate =
      canActOnMidYear &&
      (midYear?.status === "submitted_by_admin" ||
        midYear?.status === "completed")
    const canShowEndYearReinitiate =
      canActOnEndYear &&
      (endYear?.status === "submitted_by_admin" ||
        endYear?.status === "completed")

    const canInitiateMidYear = canActOnMidYear && !midYear
    const canInitiateEndYear = canActOnEndYear && !endYear

    return {
      goalPlanning,
      midYear,
      endYear,
      hasActiveGoal,
      hasCompletedGoal: isGoalPlanningLocked,
      hasCompletedMidYear: isMidYearLocked,
      isGoalPlanningLocked,
      isMidYearLocked,
      canActOnMidYear,
      canActOnEndYear,
      canShowMidYearReinitiate,
      canShowEndYearReinitiate,
      canInitiateMidYear,
      canInitiateEndYear,
      hasActiveMidYear,
      hasActiveEndYear,
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h3 className="font-semibold text-[#241763]">Admin Staff</h3>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by name or email..."
          className="w-full sm:w-72 p-2 border rounded text-sm"
          aria-label="Search admin staff"
        />
      </div>

      <div className="border rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[980px]">
          <thead className="bg-gray-100 font-bold">
            <tr>
              <th className="p-3 text-left">Name</th>
              <th className="p-3 text-left">Email</th>
              <th className="p-3 text-left">Goal Planning</th>
              <th className="p-3 text-left">Mid-Year Check-in</th>
              <th className="p-3 text-left">End-of-Year Reflection</th>
            </tr>
          </thead>
          <tbody>
            {loading || statusesLoading ? (
              <tr>
                <td colSpan={5} className="p-4 text-center text-gray-500">
                  Loading admin staff and statuses...
                </td>
              </tr>
            ) : filteredStaff.length === 0 ? (
              <tr>
                <td colSpan={5} className="p-4 text-center text-gray-500">
                  No admin staff found.
                </td>
              </tr>
            ) : (
              filteredStaff.map((admin) => {
                const status = getAdminStatus(admin)

                return (
                  <tr key={admin.id} className="border-t align-top hover:bg-gray-50">
                    <td className="p-3 font-medium whitespace-nowrap">
                      {admin.firstName} {admin.lastName}
                    </td>
                    <td className="p-3 text-gray-600">{admin.email}</td>

                    <td className="p-3">
                      <div className="flex flex-col gap-2 items-start">
                        <StatusBadge
                          label={
                            status.goalPlanning
                              ? getStatusLabel(status.goalPlanning.status)
                              : "Not Started"
                          }
                          statusClass={
                            status.goalPlanning
                              ? getStatusClass(status.goalPlanning.status)
                              : "bg-gray-100 text-gray-600"
                          }
                        />
                        {!status.goalPlanning && (
                          <button
                            type="button"
                            onClick={() => onInitiateGoalPlanning(admin)}
                            className="flex items-center gap-1 px-3 py-1 bg-[#241763] text-white rounded text-xs hover:bg-[#1a1047]"
                          >
                            <Plus className="h-3 w-3" />
                            Goal Planning
                          </button>
                        )}
                        {status.goalPlanning?.status === "submitted_by_admin" && (
                          <button
                            type="button"
                            onClick={() =>
                              onOpenConference(admin, status.goalPlanning)
                            }
                            className="px-3 py-1 bg-[#241763] text-white rounded text-xs hover:bg-[#1a1047]"
                          >
                            Complete Conference
                          </button>
                        )}
                        {status.goalPlanning?.status === "completed" && (
                          <button
                            type="button"
                            onClick={() =>
                              onViewGoalPlanning(admin, status.goalPlanning)
                            }
                            className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300"
                          >
                            View
                          </button>
                        )}
                        {status.hasActiveGoal &&
                          status.goalPlanning?.status === "initiated" && (
                            <span className="text-xs text-gray-500">
                              {status.goalPlanning?.reinitiatedAt
                                ? "Reinitiated — waiting for admin"
                                : "Waiting for admin"}
                            </span>
                          )}
                      </div>
                    </td>

                    <td className="p-3">
                      <div className="flex flex-col gap-2 items-start">
                        <StatusBadge
                          label={
                            status.midYear
                              ? getStatusLabel(status.midYear.status)
                              : "Not Started"
                          }
                          statusClass={
                            status.midYear
                              ? getStatusClass(status.midYear.status)
                              : "bg-gray-100 text-gray-600"
                          }
                        />
                        {status.canInitiateMidYear && (
                          <button
                            type="button"
                            onClick={() => onInitiateMidYear(admin)}
                            className="flex items-center gap-1 px-3 py-1 bg-[#5B9BD5] text-white rounded text-xs hover:opacity-90"
                          >
                            <Plus className="h-3 w-3" />
                            Mid-Year Check-in
                          </button>
                        )}
                        {status.isGoalPlanningLocked &&
                          status.midYear?.status === "submitted_by_admin" && (
                            <button
                              type="button"
                              onClick={() =>
                                onOpenCheckInConference(admin, status.midYear)
                              }
                              className="px-3 py-1 bg-[#241763] text-white rounded text-xs hover:bg-[#1a1047]"
                            >
                              Complete Conference
                            </button>
                          )}
                        {status.midYear?.status === "completed" && (
                          <button
                            type="button"
                            onClick={() => onViewCheckIn(admin, status.midYear)}
                            className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300"
                          >
                            View
                          </button>
                        )}
                        {!status.isGoalPlanningLocked && (
                          <span className="text-xs text-gray-500">
                            Locked until Goal Planning is completed by evaluator
                          </span>
                        )}
                        {status.isGoalPlanningLocked &&
                          status.hasActiveMidYear &&
                          status.midYear?.status === "initiated" && (
                            <span className="text-xs text-gray-500">
                              {status.midYear?.reinitiatedAt
                                ? "Reinitiated — waiting for admin"
                                : "Waiting for admin"}
                            </span>
                          )}
                      </div>
                    </td>

                    <td className="p-3">
                      <div className="flex flex-col gap-2 items-start">
                        <StatusBadge
                          label={
                            status.endYear
                              ? getStatusLabel(status.endYear.status)
                              : "Not Started"
                          }
                          statusClass={
                            status.endYear
                              ? getStatusClass(status.endYear.status)
                              : "bg-gray-100 text-gray-600"
                          }
                        />
                        {status.canInitiateEndYear && (
                          <button
                            type="button"
                            onClick={() => onInitiateEndYear(admin)}
                            className="flex items-center gap-1 px-3 py-1 bg-[#5B9BD5] text-white rounded text-xs hover:opacity-90"
                          >
                            <Plus className="h-3 w-3" />
                            End-of-Year Reflection
                          </button>
                        )}
                        {status.isGoalPlanningLocked &&
                          status.isMidYearLocked &&
                          status.endYear?.status === "submitted_by_admin" && (
                            <button
                              type="button"
                              onClick={() =>
                                onOpenCheckInConference(admin, status.endYear)
                              }
                              className="px-3 py-1 bg-[#241763] text-white rounded text-xs hover:bg-[#1a1047]"
                            >
                              Complete Conference
                            </button>
                          )}
                        {status.endYear?.status === "completed" && (
                          <button
                            type="button"
                            onClick={() => onViewCheckIn(admin, status.endYear)}
                            className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300"
                          >
                            View
                          </button>
                        )}
                        {!status.isGoalPlanningLocked && (
                          <span className="text-xs text-gray-500">
                            Locked until Goal Planning is completed by evaluator
                          </span>
                        )}
                        {status.isGoalPlanningLocked && !status.isMidYearLocked && (
                          <span className="text-xs text-gray-500">
                            Locked until Mid-Year is completed by evaluator
                          </span>
                        )}
                        {status.isGoalPlanningLocked &&
                          status.isMidYearLocked &&
                          status.hasActiveEndYear &&
                          status.endYear?.status === "initiated" && (
                            <span className="text-xs text-gray-500">
                              {status.endYear?.reinitiatedAt
                                ? "Reinitiated — waiting for admin"
                                : "Waiting for admin"}
                            </span>
                          )}
                      </div>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default AnnualGoalsAdminList
